import { ApolloCache, DefaultContext, FetchResult } from '@apollo/client';
import { MutationFunctionOptions } from '@apollo/client/react/types/types';
import { Terminal } from '@stripe/terminal-js';
import { IOption } from 'shared/components/Dropdown';
import { ConnectionStatus } from 'shared/providers/StripeTerminalProvider';
import { StripeTerminalClinicPetParent } from 'shared/providers/StripeTerminalProvider/types/StripeTerminalClinicPetParent';
import { ClearTerminalDisplayMutation, ClinicUserClinicFragment, Exact, PaymentMedium } from 'shared/types/graphql';
import {
  ENTER_AMOUNT_AND_SELECT_CLIENT,
  ENTER_AMOUNT_AND_SELECT_TERMINAL,
  SELECT_PAYMENT_METHOD_TYPE,
  SEND_DIGITAL_INVOICE,
  SEND_INVOICE_TO_TERMINAL,
} from './paymentEnumsAndVariables';

/**
 * Returns an appropriate tooltip message for the payment method dropdown based on the provided parameters.
 *
 * This function evaluates the given `currentPaymentMedium`, `amountDue`, `isValid`, `selectedPetParent`, and `terminalOption`
 * to determine which tooltip message to display to guide the user in the payment process.
 *
 * @param {IGetTooltipMessageInput} input - An object containing the following fields:
 * @param {PaymentMedium} currentPaymentMedium - The current payment medium selected by the user (e.g., StripeTerminal, StripeVirtualTerminal).
 * @param {number} amountDue - The total amount due for the transaction.
 * @param {boolean} isValid - A flag indicating whether the current state of the selected payment medium is valid.
 * @param {StripeTerminalClinicPetParent} selectedPetParent - The pet parent (customer) selected in the Stripe Terminal.
 * @param {IOption<any> | undefined} terminalOption - The selected terminal option (if applicable).
 *
 * @returns {string}
 * - Returns `SEND_INVOICE_TO_TERMINAL` if the user has selected StripeTerminal, provided an amount due, selected a terminal, and selected a pet parent.
 * - Returns `ENTER_AMOUNT_AND_SELECT_TERMINAL` if the user has selected StripeTerminal but not yet completed all required selections.
 * - Returns `SEND_DIGITAL_INVOICE` if the user has selected StripeVirtualTerminal, provided an amount due, and the state is valid.
 * - Returns `ENTER_AMOUNT_AND_SELECT_CLIENT` if the user has selected StripeVirtualTerminal but not yet completed all required selections.
 * - Returns `SELECT_PAYMENT_METHOD_TYPE` if the user has not yet selected any payment method.
 *
 * @example
 * getTooltipMessage({ currentPaymentMedium: PaymentMedium.StripeTerminal, amountDue: 100, isValid: true, selectedPetParent: somePetParent, terminalOption: someTerminalOption });
 * // Expected output: SEND_INVOICE_TO_TERMINAL
 * */

export interface IGetTooltipMessageInput {
  currentPaymentMedium: PaymentMedium;
  amountDue: number;
  isValid: boolean;
  selectedPetParent: StripeTerminalClinicPetParent | undefined;
  terminalOption: IOption | undefined;
}

export const getTooltipMessage = ({
  currentPaymentMedium,
  amountDue,
  isValid,
  selectedPetParent,
  terminalOption,
}: IGetTooltipMessageInput): string => {
  if (currentPaymentMedium) {
    if (currentPaymentMedium === PaymentMedium.StripeTerminal) {
      if (amountDue && terminalOption && selectedPetParent) return SEND_INVOICE_TO_TERMINAL;
      else return ENTER_AMOUNT_AND_SELECT_TERMINAL;
    } else if (currentPaymentMedium === PaymentMedium.StripeVirtualTerminal) {
      if (isValid && amountDue && selectedPetParent) return SEND_DIGITAL_INVOICE;
      else return ENTER_AMOUNT_AND_SELECT_CLIENT;
    } else return '';
  } else {
    return SELECT_PAYMENT_METHOD_TYPE;
  }
};

export const parseWriteBackMessage = (description: string, index: number): string => {
  // for index 1 take dollarAmount "$x.xx" and take last word of message (lastWordOfMessafe) and return it as a string `Manually apply the consumed benefits amount of ${dollarAmount} as an accounts receivable payment in ${lastWordOfMessage}.`
  // for index -1 take dollarAmount "$x.xx" and return it as a string `I have applied the ${dollarAmount} benefit`

  // regex to get dollar amount from description string
  const dollarAmount = description.match(/\$[0-9]+(\.[0-9][0-9])?/g)?.[0] ?? '$0.00';
  const lastWordOfMessage = description.split(' ').slice(-1)[0]; // "VetPay"
  if (index === 1) {
    return `Manually apply the consumed benefits amount of ${dollarAmount} as an accounts receivable payment in ${lastWordOfMessage}.`;
  } else if (index === 2) {
    return `Collect ${dollarAmount} Payment in Flow`;
  } else if (index === -1) {
    return `I have applied the ${dollarAmount} benefit`;
  } else if (index === -2) {
    return `Have you collected the ${dollarAmount} payment and closed the invoice?`;
  }
  return description;
};

export interface ICalculateClientServiceFeePercentInput {
  hasCustomizableFees: boolean;
  currentClinic: ClinicUserClinicFragment | null;
  currentPaymentMedium: PaymentMedium;
}

export const calculateClientServiceFeePercent = ({
  hasCustomizableFees,
  currentClinic,
  currentPaymentMedium,
}: ICalculateClientServiceFeePercentInput): string | undefined => {
  if (!hasCustomizableFees) {
    return '0';
  }

  if (currentPaymentMedium === PaymentMedium.StripeTerminal) {
    return currentClinic?.clinicSetting?.paymentFeeConfig?.terminalClientServiceFeePercent
      ? (currentClinic.clinicSetting.paymentFeeConfig.terminalClientServiceFeePercent * 100).toFixed(2)
      : undefined;
  }

  return currentClinic?.clinicSetting?.paymentFeeConfig?.onlineClientServiceFeePercent
    ? (currentClinic.clinicSetting.paymentFeeConfig.onlineClientServiceFeePercent * 100).toFixed(2)
    : undefined;
};

type ClearTerminalDisplayFn = (
  options?: MutationFunctionOptions<
    ClearTerminalDisplayMutation,
    Exact<{ stripeTerminalId: string }>,
    DefaultContext,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ApolloCache<any>
  >,
) => Promise<FetchResult<ClearTerminalDisplayMutation>>;

// Server-clearing utility function
export const clearDisplayServer = async (
  stripeTerminalId: string | undefined,
  clearTerminalDisplay: ClearTerminalDisplayFn,
): Promise<boolean> => {
  if (!stripeTerminalId) return false;
  try {
    const response = await clearTerminalDisplay({
      variables: { stripeTerminalId },
    });
    return response.data?.clearTerminalDisplay?.success || false;
  } catch (e) {
    console.error('Failed to clear terminal display via server', e);
    return false;
  }
};

// Local SDK-clearing utility function
export const clearDisplayLocal = async (terminal: Terminal | null): Promise<void> => {
  if (terminal?.getConnectionStatus() !== ConnectionStatus.CONNECTED) return;
  try {
    terminal.cancelCollectPaymentMethod();
  } catch (e) {
    console.error('Failed to clear terminal display via local SDK', e);
  }
};
